import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, Car, Archive, Building2, Users, CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/utils";
import { PageHeader, Kpi, Card } from "../components/core/Layout";
import { DataTable } from "../components/core/DataTable";
import { Button } from "../components/core/Button";
import { Select } from "../components/core/Field";
import { StatusBadge } from "../components/core/Badges";
import { CronStatusCard } from "../components/shared/CronStatusCard";

interface Row { tenant_id: string; name: string; slug: string; is_active: boolean; active: number; archived: number; by_category: Record<string, number>; admins: number; as_of: string; peak: number | null }
interface Snapshot { month: string; is_current: boolean; months: string[]; rows: Row[]; totals: { clients: number; active_clients: number; active: number; archived: number; admins: number; by_category: Record<string, number> }; categories: { key: string; label: string }[] }

const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-MY", { month: "long", year: "numeric" });

export default function BillingSnapshotPage() {
  const [month, setMonth] = useState("");
  const { data, isLoading } = useQuery<Snapshot>({ queryKey: ["billing-snapshot", month], queryFn: async () => (await api.get("/billing/snapshot", { params: { month: month || undefined } })).data });
  const exportCsv = () => {
    if (!data) return;
    const head = ["Client", "Status", "Active vehicles", ...data.categories.map((c) => c.label), "Archived", "Peak this month", "Admins", "As of"];
    const lines = data.rows.map((r) => [r.name, r.is_active ? "active" : "inactive", r.active, ...data.categories.map((c) => r.by_category[c.key] || 0), r.archived, r.peak ?? "", r.admins, r.as_of]);
    lines.push(["TOTAL", "", data.totals.active, ...data.categories.map((c) => data.totals.by_category[c.key] || 0), data.totals.archived, "", data.totals.admins, ""]);
    const csv = [head, ...lines].map((l) => l.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `ENFLEET-billing-${data.month}.csv`;
    a.click();
  };
  const t = data?.totals;
  return (
    <div data-testid="billing-snapshot-page">
      <PageHeader eyebrow="Billing" title="Billing Snapshot" subtitle={data ? `${monthLabel(data.month)} · ${data.is_current ? "live counts" : "last stored snapshot of the month"} · billing is driven by registered vehicle count` : "Vehicle counts per client"}
        actions={<>
          <Select data-testid="billing-month-select" value={month || data?.month || ""} onChange={(e) => setMonth(e.target.value)} className="w-48">{(data?.months || []).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}</Select>
          <Button variant="secondary" onClick={exportCsv} disabled={!data} data-testid="billing-export-button"><Download className="h-4 w-4" />Export CSV</Button>
        </>} />
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Billable vehicles" value={t?.active ?? "—"} icon={Car} tone="brand" testId="billing-kpi-active" hint={`${monthLabel(data?.month || new Date().toISOString().slice(0, 7))}`} />
        <Kpi label="Clients" value={t?.active_clients ?? "—"} icon={Building2} testId="billing-kpi-clients" delay={1} hint={`${t?.clients ?? 0} total incl. inactive`} />
        <Kpi label="Archived" value={t?.archived ?? "—"} icon={Archive} testId="billing-kpi-archived" delay={2} hint="not billed" />
        <Kpi label="Client admins" value={t?.admins ?? "—"} icon={Users} testId="billing-kpi-admins" delay={3} hint="user seats" />
      </div>
      <div className="grid gap-5 lg:grid-cols-12">
        <div className="lg:col-span-9">
          <DataTable<Row>
            testId="billing-table" loading={isLoading} rows={data?.rows || []} rowKey={(r) => r.tenant_id} rowTestId={(r) => `billing-row-${r.tenant_id}`}
            empty="No snapshot stored for this month yet."
            columns={[
              { key: "client", header: "Client", render: (r) => <Link to={`/bo/clients/${r.tenant_id}`} className="font-semibold text-slate-900 hover:underline">{r.name}</Link> },
              { key: "status", header: "Status", render: (r) => <StatusBadge status={r.is_active ? "active" : "inactive"} /> },
              { key: "active", header: "Billable", className: "text-right", render: (r) => <span className="font-display text-base font-bold" data-testid={`billing-active-${r.tenant_id}`}>{r.active}</span> },
              ...(data?.categories || []).map((c) => ({ key: c.key, header: c.label, className: "text-right", render: (r: Row) => <span className="text-xs text-slate-600">{r.by_category[c.key] || 0}</span> })),
              { key: "archived", header: "Archived", className: "text-right", render: (r) => <span className="text-xs text-slate-500">{r.archived}</span> },
              { key: "peak", header: "Peak", className: "text-right", render: (r) => <span className="text-xs">{r.peak ?? "—"}</span> },
              { key: "admins", header: "Admins", className: "text-right", render: (r) => r.admins },
              { key: "asof", header: "As of", render: (r) => <span className="text-[11px] text-slate-500">{r.as_of === "live" ? "Live" : fmtDate(r.as_of)}</span> },
            ]}
          />
          {t && (
            <div className="card-crisp mt-3 flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid="billing-totals">
              <p className="text-sm font-semibold text-slate-800">Monthly total · {t.active_clients} active client(s)</p>
              <div className="flex flex-wrap gap-4 text-sm">
                {(data?.categories || []).map((c) => <span key={c.key} className="text-slate-600">{c.label} <b className="text-slate-900">{t.by_category[c.key] || 0}</b></span>)}
                <span className="text-slate-600">Billable <b className="font-display text-base text-[color:var(--brand)]" data-testid="billing-total-active">{t.active}</b></span>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-5 lg:col-span-3">
          <CronStatusCard job="billing-snapshot" title="Daily snapshot" icon={CalendarDays} />
          <Card title="How billing counts work" testId="billing-help-card">
            <ul className="space-y-1.5 text-xs text-slate-600">
              <li>• Only <b>active</b> (non-archived) vehicles are billable.</li>
              <li>• Current month shows live counts; a snapshot is stored nightly at 23:30 MYT.</li>
              <li>• Past months show the last stored snapshot; <b>Peak</b> is the highest daily count in that month.</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
